import React, { useState, useContext } from 'react';
import { Box, Typography } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import toast from 'Utils/toast';
import AuthButton from '../../Shared/UI/AuthButton';
import AuthLayout from '../../Shared/AuthLayout';
import AuthCard from '../../Shared/AuthCard';
import DigitInputGroup from './DigitInput';
import { confirmMfaEnrollment, verifyMfaCode } from '../../services/mfaService';
import UserContext from '../../Shared/UserContext';
import { loginSucces, resetLogin } from '../../Shared/UserContext/Actions';
import fetchUserDataAfterMfa from '../../Utils/userUtils';
import { MFA_MESSAGES, getErrorMessage, showEnhancedToast } from '../../Utils/mfaMessages';
import { getTransKey, aes256gcm, JWE_KEY } from '../../Utils/crypt';

/**
 * MfaVerification - 6-digit code verification component
 * Handles both enrollment confirmation and login verification
 */
const MfaVerification = () => {
  const history = useHistory();
  const location = useLocation();
  const { dispatch } = useContext(UserContext);

  const [digitValues, setDigitValues] = useState(Array(6).fill(''));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Get data from navigation state
  const { mfaToken, mode } = location.state || {};

  // Determine if this is enrollment confirmation or login verification
  const isEnrollment = mode === 'enrollment';

  // Helper: check if all digits are filled
  const isCodeComplete = digitValues.every((digit) => digit !== '');

  // Helper: get subtitle text
  const getSubtitle = () => {
    if (error) {
      return 'Code erroné. En cas de problème, contactez-nous';
    }
    if (isEnrollment) {
      return 'Saisissez le code de confirmation de votre application d\'authentification';
    }
    return 'Saisissez le code à 6 chiffres de votre application d\'authentification';
  };

  React.useEffect(() => {
    // If no token, redirect to login
    if (!mfaToken) {
      history.push('/login');
    }
  }, [mfaToken, history]);

  // Auto-focus the first digit input when component mounts
  React.useEffect(() => {
    const timer = setTimeout(() => {
      const firstInput = document.querySelector('input[data-index="0"]');
      if (firstInput) {
        firstInput.focus();
      }
    }, 200);

    return () => clearTimeout(timer);
  }, []);

  const verifyCode = async (code) => {
    try {
      setLoading(true);
      setError(false);

      // Generate transport key for encryption setup
      const transKey = await getTransKey();

      let response;

      if (isEnrollment) {
        // Enrollment confirmation
        response = await confirmMfaEnrollment(mfaToken, code, transKey.encrypted);
      } else {
        // Login verification
        response = await verifyMfaCode(mfaToken, code, transKey.encrypted);
      }

      if (response.success && response.token) {
        // Decrypt JWE key if provided
        if (response.key) {
          const aesCipher = aes256gcm(Buffer.from(transKey.clair, 'hex'));

          // Backend encrypts 16-byte key with AES-GCM producing:
          // ciphertext (16 bytes = 32 hex) + authTag (16 bytes = 32 hex) = 64 hex chars total
          const encryptedData = response.key;

          // Split: first 32 hex chars = ciphertext, last 32 hex chars = authTag
          const ciphertext = encryptedData.slice(0, 32);
          const authTag = encryptedData.slice(32, 64);

          const decryptedKey = await aesCipher.decrypt(
            Buffer.from(ciphertext, 'hex'),
            '0000000000000000', // IV
            Buffer.from(authTag, 'hex'),
          );

          localStorage.setItem(JWE_KEY, decryptedKey);
          delete response.key;
        }

        try {
          const userData = await fetchUserDataAfterMfa(response);

          const cleanToken = response.token.replace('Bearer ', '');
          dispatch(loginSucces(userData, cleanToken));

          if (isEnrollment) {
            toast.success(MFA_MESSAGES.SUCCESS.ENROLLMENT_AND_LOGIN);
          } else {
            toast.success(MFA_MESSAGES.SUCCESS.LOGIN_SUCCESS);
          }

          if (userData?.role === 'ROLE_ADMIN') {
            history.push('/users');
          } else {
            const tutorialKey = `tutorialLastShown_${userData.username}`;
            const lastShownDate = localStorage.getItem(tutorialKey);
            const today = new Date().toDateString();
            const shouldShow = lastShownDate !== today;
            if (shouldShow) {
              sessionStorage.setItem('showTutorial', 'true');
            }
            history.push('/');
          }
        } catch (userDataError) {
          console.error('Failed to fetch user data:', userDataError);
          showEnhancedToast(toast, MFA_MESSAGES.ERRORS.USER_DATA_ERROR);
          setError(true);
          return;
        }
      } else if (isEnrollment && response.success) {
        // Enrollment completed, but may need additional steps
        toast.success(MFA_MESSAGES.SUCCESS.ENROLLMENT_COMPLETE);

        // Clear any MFA-related state before redirecting
        localStorage.removeItem('mfaToken');

        // Reset login state to clear any loading spinners
        dispatch(resetLogin());

        // Use replace instead of push to avoid back button issues
        history.replace('/login');
      } else {
        throw new Error('Réponse inattendue du serveur');
      }
    } catch (err) {
      console.error('MFA verification error:', err);
      setError(true);

      // Clear the inputs on error
      setDigitValues(Array(6).fill(''));

      // Show enhanced error message based on error type
      const errorMessage = getErrorMessage(err);
      showEnhancedToast(toast, errorMessage);

      // Focus first input after error
      setTimeout(() => {
        const firstInput = document.querySelector('input[data-index="0"]');
        if (firstInput) {
          firstInput.focus();
        }
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const handleDigitChange = (index, value) => {
    const newValues = [...digitValues];
    newValues[index] = value;
    setDigitValues(newValues);

    // Clear error state when user starts typing
    if (error) {
      setError(false);
    }
  };

  const handleComplete = async () => {
    const code = digitValues.join('');

    if (code.length !== 6) {
      return;
    }

    await verifyCode(code);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const code = digitValues.join('');

    if (code.length !== 6) {
      toast.error(MFA_MESSAGES.ERRORS.INCOMPLETE_CODE);
      return;
    }

    await verifyCode(code);
  };

  return (
    <AuthLayout>
      <AuthCard
        title="Connexion"
        subtitle={getSubtitle()}
        hasError={error}
      >
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <Box marginTop="90px" width="100%">
            <DigitInputGroup
              values={digitValues}
              onChange={handleDigitChange}
              hasError={error}
              onComplete={handleComplete}
              disabled={loading}
            />

            {/* Helpful information */}
            <Box textAlign="center" marginTop="90px" marginBottom="2px">
              <Typography
                variant="caption"
                sx={{
                  color: '#666',
                  fontSize: '18px',
                  display: 'block',
                  lineHeight: '1.4',
                }}
              >
                {' '}
                {MFA_MESSAGES.INFO.TOTP_GUIDANCE.message}
              </Typography>
            </Box>

            <AuthButton disabled={!isCodeComplete} loading={loading} type="submit" sx={{ mt: '-4px', '&.Mui-disabled': { backgroundColor: '#007BC6', color: '#fff' } }}>
              Continuer
            </AuthButton>
          </Box>
        </form>
      </AuthCard>
    </AuthLayout>
  );
};

export default MfaVerification;
